import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AiAssistant } from "@/components/AiAssistant";
import { Head } from "./sections/shared";
import {
  LayoutDashboard, LayoutTemplate, ShoppingBag, Users, FolderKanban, FileText,
  Settings, Home, LogOut, CreditCard, BarChart3, MessageSquare, Inbox,
  Brain, Network, Bot, ShieldCheck, Archive, Download, Receipt, History, Repeat, Target, Package, ClipboardList, BookOpen, Search, CalendarCheck, Landmark, Megaphone, Ticket, MessagesSquare, Sparkles,
} from "lucide-react";

import { Overview } from "./sections/Overview";
import { NotificationBell } from "./sections/NotificationBell";
import { Requests } from "./sections/Requests";
import { LeadsInbox } from "./sections/LeadsInbox";
import { Projects } from "./sections/Projects";
import { Clients } from "./sections/Clients";
import { AdminMessages } from "./sections/Messages";
import { Proposals } from "./sections/Proposals";
import { Templates } from "./sections/Templates";
import { Orders } from "./sections/Orders";
import { Payments } from "./sections/Payments";
import { Invoices } from "./sections/Invoices";
import { AIHistory } from "./sections/AIHistory";
import { AICenter } from "./sections/AICenter";
import { Reports } from "./sections/Reports";
import { UsersPanel } from "./sections/UsersPanel";
import { SettingsView } from "./sections/Settings";
import { RecordsCenter } from "./sections/RecordsCenter";
import { ProductDownloads } from "./sections/ProductDownloads";
import { Subscriptions } from "./sections/Subscriptions";
import { Leads } from "./sections/Leads";
import { CustomBuildInquiries } from "./sections/CustomBuildInquiries";
import { Consultations } from "./sections/Consultations";
import { AssistantConversations } from "./sections/AssistantConversations";
import { AiBots } from "./sections/AiBots";
import { AddonRequests } from "./sections/AddonRequests";
import { BusinessPlan } from "./sections/BusinessPlan";
import { GrantFinder } from "./sections/GrantFinder";
import { AIConcierge } from "./sections/AIConcierge";
import { SeoCenter } from "./sections/SeoCenter";
import { Announcements } from "./sections/Announcements";
import { GrowthMetrics } from "./sections/GrowthMetrics";
import { VeteranLeads } from "./sections/VeteranLeads";
import { CohortRequests } from "./sections/CohortRequests";
import { DiscountCodes } from "./sections/DiscountCodes";

const NAV = [
  ["overview", "Dashboard", LayoutDashboard],
  ["growth", "Growth Metrics", BarChart3],
  ["projects", "Projects", FolderKanban],
  ["clients", "Clients", Users],
  ["announcements", "Announcements", Megaphone],
  ["messages", "Messages", MessageSquare],
  ["requests", "Leads & Requests", Inbox],
  ["inquiries", "Custom-Build Inquiries", Target],
  ["consultations", "Consultations", CalendarCheck],
  ["veteran-leads", "Veteran Leads", ShieldCheck],
  ["cohort-requests", "Cohort Requests", Users],
  ["discount-codes", "Discount Codes", Ticket],
  ["addon-requests", "Add-on Requests", Package],
  ["business-plan", "Business Plan", ClipboardList],
  ["grant-finder", "Grant Finder", Landmark],
  ["ai-concierge", "AI Concierge", Bot],
  ["ai-bots", "AI Bots (Client)", Sparkles],
  ["conversations", "Concierge Chats", MessagesSquare],
  ["proposals", "Proposals", FileText],
  ["templates", "Templates", LayoutTemplate],
  ["seo", "SEO Center", Search],
  ["downloads", "Product Downloads", Download],
  ["orders", "Orders", ShoppingBag],
  ["payments", "Payments", CreditCard],
  ["invoices", "Invoices", Receipt],
  ["subscriptions", "Plans & Subs", Repeat],
  ["ai", "AI Center", Brain],
  ["aihistory", "AI History", History],
  ["studio", "AIPOS Studio", Network],
  ["assistant", "AI Assistant", Bot],
  ["reports", "Reports", BarChart3],
  ["users", "Team & Users", ShieldCheck],
  ["records", "Records Center", Archive],
  ["settings", "Settings", Settings],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = NAV.map(([k]) => k);
  const initial = searchParams.get("view");
  const [view, setView] = useState(validKeys.includes(initial) ? initial : "overview");
  const [unread, setUnread] = useState(0);
  const [inqNew, setInqNew] = useState(0);
  const go = (k) => {
    if (k === "studio") return navigate("/admin/studio");
    setView(k);
    setSearchParams(k === "overview" ? {} : { view: k }, { replace: true });
  };
  useEffect(() => {
    const v = searchParams.get("view");
    if (validKeys.includes(v) && v !== view) setView(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const loadUnread = useCallback(() => api.get("/messages/unread").then((r) => setUnread(r.data.total)).catch(() => {}), []);
  const loadInq = useCallback(() => api.get("/admin/inquiries/counts").then((r) => setInqNew(r.data.new)).catch(() => {}), []);
  useEffect(() => { loadUnread(); loadInq(); const t = setInterval(() => { loadUnread(); loadInq(); }, 30000); return () => clearInterval(t); }, [loadUnread, loadInq, view]);

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex fixed left-0 h-screen w-64 bg-navy border-r border-white/10 flex-col z-40">
        <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-white/10">
          <div className="w-9 h-9 border border-gold flex items-center justify-center font-heading font-bold text-gold">ATF</div>
          <div><div className="font-heading text-white font-semibold leading-none">WebWorks</div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Admin</div></div>
        </Link>
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => go(k)} data-testid={`admin-nav-${k}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${view === k ? "bg-gold/15 text-gold border-l-2 border-gold" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon size={17} /> {l}
              {k === "messages" && unread > 0 && <span className="ml-auto text-[10px] bg-gold text-navy px-2 py-0.5 font-bold" data-testid="admin-unread-badge">{unread}</span>}
              {k === "inquiries" && inqNew > 0 && <span className="ml-auto text-[10px] bg-gold text-navy px-2 py-0.5 font-bold" data-testid="admin-inquiries-badge">{inqNew}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <a href="/admin-training-manual.html" target="_blank" rel="noopener noreferrer" data-testid="admin-training-manual" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:text-gold"><BookOpen size={16} /> Training Manual</a>
          <Link to="/" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:text-white"><Home size={16} /> View Site</Link>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:text-white"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Mobile nav */}
        <div className="lg:hidden flex gap-1 overflow-x-auto p-3 border-b border-white/10 bg-navy sticky top-0 z-40">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => go(k)} className={`flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap ${view === k ? "bg-gold text-navy" : "text-gray-400"}`}><Icon size={14} />{l}</button>
          ))}
        </div>
        <div className="p-6 md:p-10 max-w-6xl">
          <div className="flex items-center justify-end mb-6 -mt-1" data-testid="admin-topbar">
            <NotificationBell onNav={go} />
          </div>
          {view === "overview" && <Overview onNav={go} />}
          {view === "growth" && <GrowthMetrics />}
          {view === "requests" && <LeadsInbox />}
          {view === "inquiries" && <CustomBuildInquiries />}
          {view === "consultations" && <Consultations />}
          {view === "veteran-leads" && <VeteranLeads />}
          {view === "cohort-requests" && <CohortRequests />}
          {view === "discount-codes" && <DiscountCodes />}
          {view === "projects" && <Projects />}
          {view === "clients" && <Clients onNav={go} />}
          {view === "announcements" && <Announcements />}
          {view === "messages" && <AdminMessages onRead={loadUnread} />}
          {view === "proposals" && <Proposals />}
          {view === "templates" && <Templates />}
          {view === "seo" && <SeoCenter />}
          {view === "downloads" && <ProductDownloads />}
          {view === "orders" && <Orders />}
          {view === "payments" && <Payments />}
          {view === "invoices" && <Invoices onNav={go} />}
          {view === "subscriptions" && <Subscriptions />}
          {view === "leads" && <Leads />}
          {view === "ai" && <AICenter />}
          {view === "aihistory" && <AIHistory />}
          {view === "conversations" && <AssistantConversations />}
          {view === "addon-requests" && <AddonRequests />}
          {view === "business-plan" && <BusinessPlan />}
          {view === "grant-finder" && <GrantFinder />}
          {view === "ai-concierge" && <AIConcierge />}
          {view === "ai-bots" && <AiBots />}
          {view === "assistant" && (
            <div>
              <Head title="AI Assistant" desc="Chat with AI to draft website copy, SEO, branding and more." />
              <AiAssistant scope="admin" />
            </div>
          )}
          {view === "reports" && <Reports />}
          {view === "users" && <UsersPanel currentUser={user} />}
          {view === "records" && <RecordsCenter />}
          {view === "settings" && <SettingsView user={user} />}
        </div>
      </div>
    </div>
  );
}

